import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { 
  X, UserMinus, CheckCircle, AlertTriangle, Users, Briefcase, 
  ChevronRight, ChevronLeft, Loader2, Calendar, Clock, Search,
  RefreshCw, FileText, Package, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Progress } from './ui/progress';
import { getTodayLocal } from '../lib/dateUtils';

/**
 * OffboardingWizard - A multi-step wizard for employee offboarding
 * Handles task reassignment, POC reassignment, and knowledge transfer
 */
export default function OffboardingWizard({ 
  employee, 
  isOpen, 
  onClose, 
  onComplete 
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [offboardingData, setOffboardingData] = useState(null);
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [notes, setNotes] = useState('');
  
  // Task reassignment state
  const [pendingTasks, setPendingTasks] = useState([]);
  const [selectedTaskAssignee, setSelectedTaskAssignee] = useState('');
  const [taskActions, setTaskActions] = useState({}); // {taskId: {action, assigneeId}}
  const [suggestedReplacements, setSuggestedReplacements] = useState([]);
  
  // POC reassignment state
  const [pendingPocs, setPendingPocs] = useState([]);
  const [pocActions, setPocActions] = useState({}); // {clientId_dept: newPocId}
  const [employeesByDepartment, setEmployeesByDepartment] = useState({}); // {dept: [employees]}
  
  // Knowledge transfer state
  const [knowledgeNotes, setKnowledgeNotes] = useState('');
  const [equipmentStatus, setEquipmentStatus] = useState({
    laptop_returned: false,
    access_cards_returned: false,
    notes: ''
  });
  
  // Completing state
  const [completing, setCompleting] = useState(false);

  const checkExistingOffboarding = useCallback(async () => {
    if (!employee?._id) return;
    
    try {
      const response = await adminApi.get(`/api/admin/employees/${employee._id}/offboarding`);
      if (response.data.status === 'in_progress' || response.data.status === 'pending_review') {
        // Load existing offboarding
        loadOffboardingDetails(response.data.offboarding_id);
      } else if (response.data.status === 'none' || response.data.status === 'completed') {
        // No active offboarding - check if this is a retroactive case
        // Pre-populate last_working_day if employee already has one set
        const existingLastDay = employee?.employment?.last_working_day || employee?.employment?.last_day;
        if (existingLastDay) {
          // Check if the last working day is in the past
          const today = getTodayLocal();
          if (existingLastDay < today) {
            setLastWorkingDay(existingLastDay);
            setIsRetroactive(true);
          }
        }
      }
    } catch (error) {
      // API error - still check if this is a retroactive case
      // Pre-populate last_working_day if employee already has one set
      const existingLastDay = employee?.employment?.last_working_day || employee?.employment?.last_day;
      if (existingLastDay) {
        const today = getTodayLocal();
        if (existingLastDay < today) {
          setLastWorkingDay(existingLastDay);
          setIsRetroactive(true);
        }
      }
    }
  }, [employee?._id, employee?.employment?.last_working_day, employee?.employment?.last_day]);

  // Track if this is a retroactive offboarding
  const [isRetroactive, setIsRetroactive] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen && employee) {
      setStep(1);
      setOffboardingData(null);
      setLastWorkingDay('');
      setNotes('');
      setPendingTasks([]);
      setPendingPocs([]);
      setTaskActions({});
      setPocActions({});
      setSuggestedReplacements([]);
      setEmployeesByDepartment({});
      setKnowledgeNotes('');
      setEquipmentStatus({ laptop_returned: false, access_cards_returned: false, notes: '' });
      setIsRetroactive(false);
      
      // Check if offboarding already exists
      checkExistingOffboarding();
    }
  }, [isOpen, employee, checkExistingOffboarding]);

  const loadOffboardingDetails = async (offboardingId) => {
    setLoading(true);
    try {
      const response = await adminApi.get(`/api/admin/offboarding/${offboardingId}`);
      const data = response.data;
      
      setOffboardingData(data);
      setLastWorkingDay(data.last_working_day);
      setPendingTasks(data.pending_tasks || []);
      setPendingPocs(data.pending_pocs || []);
      setSuggestedReplacements(data.suggested_replacements || []);
      setEmployeesByDepartment(data.employees_by_department || {});
      setKnowledgeNotes(data.knowledge_transfer?.notes || '');
      setEquipmentStatus(data.equipment_status || { laptop_returned: false, access_cards_returned: false, notes: '' });
      
      // If tasks/POCs already handled, skip to appropriate step
      if (data.pending_tasks?.length === 0 && data.pending_pocs?.length === 0) {
        setStep(3);
      } else if (data.pending_tasks?.length === 0) {
        setStep(2);
      }
    } catch (error) {
      toast.error('Failed to load offboarding details');
    } finally {
      setLoading(false);
    }
  };

  const initiateOffboarding = async () => {
    if (!lastWorkingDay) {
      toast.error('Please select the last working day');
      return;
    }

    setInitiating(true);
    try {
      const response = await adminApi.post(`/api/admin/employees/${employee._id}/offboarding`, {
        last_working_day: lastWorkingDay,
        notes: notes,
        is_retroactive: isRetroactive  // Allow past dates for employees who already left
      });
      
      setOffboardingData(response.data);
      setPendingTasks(response.data.pending_items?.tasks?.list || []);
      setPendingPocs(response.data.pending_items?.client_pocs?.assignments || []);
      setSuggestedReplacements(response.data.suggested_replacements || []);
      setEmployeesByDepartment(response.data.employees_by_department || {});
      
      toast.success('Offboarding initiated successfully');
      
      // Move to next step
      if (response.data.pending_items?.tasks?.total > 0) {
        setStep(2);
      } else if (response.data.pending_items?.client_pocs?.total > 0) {
        setStep(3);
      } else {
        setStep(4);
      }
    } catch (error) {
      const errorData = error.response?.data?.detail;
      if (typeof errorData === 'object' && errorData.error === 'offboarding_in_progress') {
        toast.info('Offboarding already in progress. Loading...');
        loadOffboardingDetails(errorData.offboarding_id);
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to initiate offboarding');
      }
    } finally {
      setInitiating(false);
    }
  };

  const handleBulkReassignTasks = async () => {
    if (!selectedTaskAssignee) {
      toast.error('Please select an employee to reassign tasks to');
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.post(`/api/admin/offboarding/${offboardingData.offboarding_id}/bulk-reassign-tasks`, {
        new_assignee_id: selectedTaskAssignee
      });
      
      toast.success(`${response.data.reassigned_count} tasks reassigned to ${response.data.new_assignee.name}`);
      setPendingTasks([]);
      
      // Move to POC step or finalize
      if (pendingPocs.length > 0) {
        setStep(3);
      } else {
        setStep(4);
      }
    } catch (error) {
      toast.error('Failed to reassign tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleTaskAction = async (taskId, action, newAssigneeId) => {
    setLoading(true);
    try {
      await adminApi.post(`/api/admin/offboarding/${offboardingData.offboarding_id}/reassign-task`, {
        task_id: taskId,
        action: action,
        new_assignee_id: newAssigneeId || selectedTaskAssignee
      });
      
      // Remove task from pending list
      setPendingTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task handled successfully');
      
      // Check if we should move to next step
      if (pendingTasks.length <= 1) {
        if (pendingPocs.length > 0) {
          setStep(3);
        } else {
          setStep(4);
        }
      }
    } catch (error) {
      toast.error('Failed to handle task');
    } finally {
      setLoading(false);
    }
  };

  const handlePocReassign = async (clientId, department, newPocId) => {
    if (!newPocId) {
      toast.error('Please select a new POC');
      return;
    }

    setLoading(true);
    try {
      await adminApi.post(`/api/admin/offboarding/${offboardingData.offboarding_id}/reassign-poc`, {
        client_id: clientId,
        department: department,
        new_poc_id: newPocId
      });
      
      // Remove from pending list
      setPendingPocs(prev => prev.filter(p => !(p.client_id === clientId && p.department === department)));
      toast.success('POC reassigned successfully');
      
      // Check if we should move to next step
      if (pendingPocs.length <= 1) {
        setStep(4);
      }
    } catch (error) {
      toast.error('Failed to reassign POC');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (forceComplete = false) => {
    setCompleting(true);
    try {
      // Update knowledge transfer notes first
      if (knowledgeNotes) {
        await adminApi.put(`/api/admin/offboarding/${offboardingData.offboarding_id}/knowledge-transfer`, {
          notes: knowledgeNotes,
          completed: true
        });
      }
      
      // Update equipment status
      await adminApi.put(`/api/admin/offboarding/${offboardingData.offboarding_id}/equipment`, equipmentStatus);
      
      // Complete the offboarding
      const response = await adminApi.post(`/api/admin/offboarding/${offboardingData.offboarding_id}/complete`, {
        knowledge_transfer_notes: knowledgeNotes,
        equipment_notes: equipmentStatus.notes,
        force_complete: forceComplete
      });
      
      toast.success('Offboarding completed successfully!');
      
      if (response.data.warnings?.length > 0) {
        response.data.warnings.forEach(w => toast.warning(w));
      }
      
      onComplete && onComplete();
      onClose();
    } catch (error) {
      const errorData = error.response?.data?.detail;
      if (typeof errorData === 'object' && errorData.error === 'Incomplete handover') {
        // Show confirmation dialog for force complete
        if (window.confirm(`There are still ${errorData.pending_tasks} pending tasks and ${errorData.pending_pocs} pending POC assignments. Do you want to force complete? Remaining tasks will be marked as orphaned.`)) {
          handleComplete(true);
        }
      } else {
        toast.error(errorData?.message || errorData || 'Failed to complete offboarding');
      }
    } finally {
      setCompleting(false);
    }
  };

  const calculateProgress = () => {
    if (!offboardingData) return 0;
    
    const initialTasks = offboardingData.pending_items?.tasks?.total || offboardingData.tasks?.initial || 0;
    const initialPocs = offboardingData.pending_items?.client_pocs?.total || offboardingData.pocs?.initial || 0;
    const total = initialTasks + initialPocs;
    
    if (total === 0) return 100;
    
    const handledTasks = initialTasks - pendingTasks.length;
    const handledPocs = initialPocs - pendingPocs.length;
    
    return Math.round(((handledTasks + handledPocs) / total) * 100);
  };

  // Get minimum date (today)
  const getMinDate = () => {
    return getTodayLocal();
  };

  if (!employee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <UserMinus className="w-5 h-5 text-orange-500" />
            <span>Employee Offboarding: {employee?.personal?.full_name || employee?.name || 'Employee'}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Progress indicator */}
        {offboardingData && (
          <div className="px-1 py-2 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-600">Progress: {calculateProgress()}% Complete</span>
              {offboardingData.days_remaining !== undefined && (
                <span className={`text-sm font-medium ${offboardingData.days_remaining <= 3 ? 'text-red-600' : 'text-neutral-600'}`}>
                  <Clock className="w-4 h-4 inline mr-1" />
                  {offboardingData.days_remaining} days remaining
                </span>
              )}
            </div>
            <Progress value={calculateProgress()} className="h-2" />
          </div>
        )}

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 py-3 border-b flex-shrink-0">
          {[
            { num: 1, label: 'Setup' },
            { num: 2, label: 'Tasks' },
            { num: 3, label: 'POCs' },
            { num: 4, label: 'Finalize' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              {idx > 0 && <div className="w-8 h-0.5 bg-neutral-200" />}
              <div 
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  step === s.num 
                    ? 'bg-pink-100 text-pink-700' 
                    : step > s.num 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-neutral-100 text-neutral-500'
                }`}
              >
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Content area - scrollable */}
        <div className="flex-1 overflow-y-auto px-1 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
            </div>
          ) : (
            <>
              {/* Step 1: Setup - Select last working day */}
              {step === 1 && (
                <div className="space-y-6">
                  {/* Retroactive offboarding notice */}
                  {isRetroactive && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-red-800">Retroactive Offboarding Required</h4>
                          <p className="text-sm text-red-700 mt-1">
                            This employee&apos;s last working day ({lastWorkingDay}) has already passed, but the formal offboarding process was never completed. 
                            Please complete the handover now to reassign any pending tasks and client POC roles.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isRetroactive && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                      <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-orange-800">Important</h4>
                          <p className="text-sm text-orange-700 mt-1">
                            Starting the offboarding process will require you to reassign all pending tasks and client POC assignments before setting the employee&apos;s last working day.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-neutral-500" />
                      Set Last Working Day
                    </h3>
                    
                    {/* Retroactive toggle — explicit user control to allow past dates */}
                    <label className="flex items-start gap-3 mb-5 p-3 bg-amber-50/60 border border-amber-200/70 rounded-xl cursor-pointer hover:bg-amber-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={isRetroactive}
                        onChange={(e) => setIsRetroactive(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-pink-600 cursor-pointer"
                        data-testid="offboarding-retroactive-toggle"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900">This employee has already left</p>
                        <p className="text-xs text-amber-700 mt-0.5">Enable to pick a past date and record offboarding retroactively (no notifications sent for the elapsed period).</p>
                      </div>
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Last Working Day *
                        </label>
                        <input
                          type="date"
                          value={lastWorkingDay}
                          onChange={(e) => {
                            const v = e.target.value;
                            setLastWorkingDay(v);
                            // Auto-suggest retroactive when user picks a past date
                            if (v && v < getMinDate() && !isRetroactive) {
                              setIsRetroactive(true);
                              toast.info('Past date detected — retroactive offboarding mode enabled');
                            }
                          }}
                          min={isRetroactive ? undefined : getMinDate()}
                          className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 focus:border-transparent"
                          data-testid="offboarding-last-day-input"
                        />
                        {isRetroactive && lastWorkingDay && lastWorkingDay < getMinDate() && (
                          <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Recording offboarding for a past date — {Math.floor((new Date(getMinDate()) - new Date(lastWorkingDay)) / 86400000)} day(s) ago
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Notes (Optional)
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Reason for leaving, special instructions..."
                          className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 focus:border-transparent"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Employee summary */}
                  <div className="bg-white/40 border border-white/40 rounded-xl p-4">
                    <h4 className="font-medium text-neutral-700 mb-2">Employee Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-neutral-500">Name:</span>
                        <p className="font-medium">{employee?.personal?.full_name || employee?.name}</p>
                      </div>
                      <div>
                        <span className="text-neutral-500">Email:</span>
                        <p className="font-medium">{employee?.work_email}</p>
                      </div>
                      <div>
                        <span className="text-neutral-500">Department:</span>
                        <p className="font-medium">{Array.isArray(employee?.employment?.departments) ? employee.employment.departments.join(', ') : employee?.employment?.departments || '-'}</p>
                      </div>
                      <div>
                        <span className="text-neutral-500">Designation:</span>
                        <p className="font-medium">{employee?.employment?.designation || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Task Reassignment */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-800">Task Reassignment</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          {pendingTasks.length} pending task{pendingTasks.length !== 1 ? 's' : ''} need to be reassigned, cancelled, or marked as complete.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bulk reassign option */}
                  <div className="glass-card rounded-xl p-4">
                    <h4 className="font-medium mb-3">Quick Action: Reassign All Tasks</h4>
                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm text-neutral-600 mb-1">Reassign to:</label>
                        <select
                          value={selectedTaskAssignee}
                          onChange={(e) => setSelectedTaskAssignee(e.target.value)}
                          className="w-full px-3 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                          data-testid="bulk-task-assignee-select"
                        >
                          <option value="">Select employee...</option>
                          {suggestedReplacements.map(emp => (
                            <option key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                              {emp.name} ({emp.task_load || emp.current_task_load} tasks) {emp.capacity === 'available' ? '⭐' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        onClick={handleBulkReassignTasks}
                        disabled={!selectedTaskAssignee || loading}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="bulk-reassign-tasks-btn"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Reassign All {pendingTasks.length} Tasks
                      </Button>
                    </div>
                  </div>

                  {/* Individual task list */}
                  {pendingTasks.length > 0 && (
                    <div className="glass-card rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-white/40 border-b">
                        <h4 className="font-medium">Or Handle Individually</h4>
                      </div>
                      <div className="divide-y max-h-[300px] overflow-y-auto">
                        {pendingTasks.map(task => (
                          <div key={task.id} className="p-4 hover:bg-white/60">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium text-[#1a1a2e] truncate">{task.title}</h5>
                                <div className="flex flex-wrap gap-2 mt-1 text-xs">
                                  <span className="px-2 py-0.5 bg-neutral-100 rounded">{task.client_name || 'No Client'}</span>
                                  <span className={`px-2 py-0.5 rounded ${
                                    task.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                    task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-neutral-100 text-neutral-700'
                                  }`}>
                                    {task.priority}
                                  </span>
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{task.status}</span>
                                  {task.due_date && (
                                    <span className="text-neutral-500">Due: {task.due_date}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSingleTaskAction(task.id, 'complete_early')}
                                  className="text-green-600 border-green-300 hover:bg-green-50"
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Complete
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSingleTaskAction(task.id, 'cancel')}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pendingTasks.length === 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <h4 className="font-medium text-green-800">All Tasks Handled!</h4>
                      <p className="text-sm text-green-700 mt-1">You can proceed to the next step.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: POC Reassignment */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-purple-800">Client POC Reassignment</h4>
                        <p className="text-sm text-purple-700 mt-1">
                          {pendingPocs.length} client POC assignment{pendingPocs.length !== 1 ? 's' : ''} need to be reassigned.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* POC list */}
                  {pendingPocs.length > 0 && (
                    <div className="space-y-4">
                      {pendingPocs.map((poc, idx) => (
                        <div key={`${poc.client_id}_${poc.department}`} className="glass-card rounded-xl p-4">
                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div>
                              <h5 className="font-medium text-[#1a1a2e]">{poc.client_name}</h5>
                              <p className="text-sm text-neutral-500">Department: {poc.department}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <select
                                value={pocActions[`${poc.client_id}_${poc.department}`] || ''}
                                onChange={(e) => setPocActions(prev => ({
                                  ...prev,
                                  [`${poc.client_id}_${poc.department}`]: e.target.value
                                }))}
                                className="px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                              >
                                <option value="">Select new POC...</option>
                                {/* Show employees from the POC's department */}
                                {(employeesByDepartment[poc.department] || []).map(emp => (
                                  <option key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                                    {emp.name}
                                  </option>
                                ))}
                                {/* Fallback to suggested replacements if no department-specific employees */}
                                {(!employeesByDepartment[poc.department] || employeesByDepartment[poc.department].length === 0) && 
                                  suggestedReplacements.map(emp => (
                                    <option key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                                      {emp.name}
                                    </option>
                                  ))
                                }
                              </select>
                              <Button
                                size="sm"
                                onClick={() => handlePocReassign(
                                  poc.client_id, 
                                  poc.department, 
                                  pocActions[`${poc.client_id}_${poc.department}`]
                                )}
                                disabled={!pocActions[`${poc.client_id}_${poc.department}`] || loading}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                Reassign
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {pendingPocs.length === 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <h4 className="font-medium text-green-800">All POCs Reassigned!</h4>
                      <p className="text-sm text-green-700 mt-1">You can proceed to finalize the offboarding.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Finalize */}
              {step === 4 && (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-neutral-500" />
                      Offboarding Summary
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-blue-700">
                          {offboardingData?.tasks?.initial || offboardingData?.pending_items?.tasks?.total || 0}
                        </div>
                        <div className="text-sm text-blue-600">Tasks Handled</div>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-purple-700">
                          {offboardingData?.pocs?.initial || offboardingData?.pending_items?.client_pocs?.total || 0}
                        </div>
                        <div className="text-sm text-purple-600">POCs Reassigned</div>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-orange-700">
                          {lastWorkingDay}
                        </div>
                        <div className="text-sm text-orange-600">Last Working Day</div>
                      </div>
                    </div>
                  </div>

                  {/* Knowledge Transfer */}
                  <div className="glass-card rounded-xl p-6">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-neutral-500" />
                      Knowledge Transfer Notes
                    </h4>
                    <textarea
                      value={knowledgeNotes}
                      onChange={(e) => setKnowledgeNotes(e.target.value)}
                      placeholder="Document important information for the successor(s)... e.g., passwords shared, project status, client preferences, pending items..."
                      className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                      rows={4}
                    />
                  </div>

                  {/* Equipment Checklist */}
                  <div className="glass-card rounded-xl p-6">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4 text-neutral-500" />
                      Equipment Checklist
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={equipmentStatus.laptop_returned}
                          onChange={(e) => setEquipmentStatus(prev => ({ ...prev, laptop_returned: e.target.checked }))}
                          className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                        />
                        <span>Laptop returned</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={equipmentStatus.access_cards_returned}
                          onChange={(e) => setEquipmentStatus(prev => ({ ...prev, access_cards_returned: e.target.checked }))}
                          className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                        />
                        <span>Access cards returned</span>
                      </label>
                      <div className="mt-3">
                        <label className="block text-sm text-neutral-600 mb-1">Equipment Notes</label>
                        <input
                          type="text"
                          value={equipmentStatus.notes}
                          onChange={(e) => setEquipmentStatus(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Serial numbers, additional items..."
                          className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Warning about remaining items */}
                  {(pendingTasks.length > 0 || pendingPocs.length > 0) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-yellow-800">Pending Items</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            There are still {pendingTasks.length} pending task(s) and {pendingPocs.length} pending POC assignment(s). 
                            You can force complete, which will mark remaining tasks as orphaned.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Final confirmation */}
                  <div className="bg-white/40 border border-white/40 rounded-xl p-4">
                    <p className="text-sm text-neutral-700">
                      By completing this offboarding:
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-neutral-600">
                      <li>• {employee?.personal?.full_name || employee?.name}&apos;s last working day will be set to {lastWorkingDay}</li>
                      <li>• They will not be able to log in after {lastWorkingDay}</li>
                      <li>• A farewell notice will appear on the Notice Board</li>
                      <li>• All reassignments will be logged for audit</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with navigation buttons */}
        <DialogFooter className="flex-shrink-0 border-t pt-4 gap-2">
          {step > 1 && offboardingData && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={loading || completing}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
          )}
          
          <div className="flex-1" />
          
          <Button variant="outline" onClick={onClose} disabled={loading || completing}>
            Cancel
          </Button>
          
          {step === 1 && !offboardingData && (
            <Button
              onClick={initiateOffboarding}
              disabled={!lastWorkingDay || initiating}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="start-offboarding-btn"
            >
              {initiating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Initiating...
                </>
              ) : (
                <>
                  Start Offboarding
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          )}
          
          {step === 1 && offboardingData && (
            <Button
              onClick={() => pendingTasks.length > 0 ? setStep(2) : pendingPocs.length > 0 ? setStep(3) : setStep(4)}
              className="bg-pink-600 hover:bg-pink-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          {step === 2 && pendingTasks.length === 0 && (
            <Button
              onClick={() => pendingPocs.length > 0 ? setStep(3) : setStep(4)}
              className="bg-pink-600 hover:bg-pink-700"
            >
              Next Step
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          {step === 3 && pendingPocs.length === 0 && (
            <Button
              onClick={() => setStep(4)}
              className="bg-pink-600 hover:bg-pink-700"
            >
              Finalize
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          {step === 4 && (
            <Button
              onClick={() => handleComplete(false)}
              disabled={completing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="complete-offboarding-btn"
            >
              {completing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete Offboarding
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
